import { useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Card,
  CardMedia,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { getCaseAssetsApi } from 'api/cases/query';
import { MODALS_STORE, TASKS_MODAL_ID } from 'constants/ui/modal';
import { useLocalization } from 'localization';
import { TaskCategory, TaskStatus, VirtualStagingType } from 'api';
import { virtualStagingApi } from 'api/tasks/query';
import SkeletonContainer from 'components/v2/container/SkeletonContainer';
import { DEFAULT_IMG_SOURCE } from 'modules/material/MaterialItem';
import CardTitleHeader from 'components/v2/card/CardTitleHeader';
import { colors } from 'theme/mui/colors';
import { interiorOptions } from 'constants/options/caseOptions';
import Materials from 'modules/v2/material/materials/Materials';

const SelectItem = () => {
  const { t, f } = useLocalization();

  const { getModalData, setModalData } = MODALS_STORE.tasks();

  const caseId = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.caseId ?? '';
  const materialType = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.materialType;
  const assetIds = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.materialIds ?? [];
  const taskStatus = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.status;
  const { data } = getCaseAssetsApi(caseId);

  const filterAssets = useMemo(
    () => data?.materials?.filter(({ type }) => type === materialType) ?? [],
    [materialType]
  );

  const handleSelectImage = (id: string) => {
    const isSelected = assetIds.includes(id);
    const currentSelectedImg = filterAssets.find(item => item.id);
    if (isSelected) {
      setModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE, {
        ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE),
        virtualStaging: {
          ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging,
          materialIds: [],
          thumbnailUrl: '',
          thumbnailDescription: '',
        },
      });
    } else if (assetIds.length < 1) {
      setModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE, {
        ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE),
        virtualStaging: {
          ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging,
          materialIds: [id],
          thumbnailUrl: currentSelectedImg?.thumbnailUrl,
          thumbnailDescription: currentSelectedImg?.description,
        },
      });
    }
  };

  return (
    <Grid container spacing="0.75rem">
      {filterAssets?.length ? (
        <Materials
          visualMaterials={filterAssets}
          disabled={taskStatus === TaskStatus.Complete}
          onItemClick={handleSelectImage}
          currentSelectedId={assetIds}
        />
      ) : (
        <Stack
          sx={{
            width: '100vw',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6">{t('modals.virtualStaging.emptyList')}</Typography>
        </Stack>
      )}
    </Grid>
  );
};

const RoomType = () => {
  const { t } = useLocalization();
  const { getModalData, setModalData } = MODALS_STORE.tasks();

  const taskType = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.type;
  const imgSrc = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.thumbnailUrl ?? '';
  const currentTypeRoom = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.roomType;
  const activeStep = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.activeStep ?? 0;
  const virtualStagingType: VirtualStagingType =
    taskType === TaskCategory.VirtualStagingCommercial || taskType === TaskCategory.VirtaulStagingCommercial720
      ? 'Commercial'
      : 'Residential';

  const { data: virtualStagingData, isLoading: virtualStagingLoading } = virtualStagingApi(
    virtualStagingType,
    activeStep === 1
  );

  const handleRoomType = (type: string, url: string) => {
    setModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE, {
      ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE),
      virtualStaging: {
        ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE).virtualStaging,
        roomType: type,
        roomTypeUrl: url,
      },
    });
  };

  return (
    <SkeletonContainer columns={{ xs: 1 }} rows={{ xs: 1 }} loading={virtualStagingLoading}>
      <Stack direction={{ xs: 'column', md: 'row' }} gap="1.25rem">
        <Card sx={{ flex: 1 }}>
          <CardTitleHeader title={t('modals.virtualStaging.summaryStep.yourChoiceText')} />
          <CardMedia
            component="img"
            height="200"
            width="200"
            onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)}
            image={imgSrc}
          />
        </Card>
        <Card sx={{ flex: 3 }}>
          <CardTitleHeader title={t('modals.virtualStaging.selectRoomStep.title')} />
          <Grid container gap="1.25rem">
            {virtualStagingData?.length ? (
              virtualStagingData.map(img => {
                return (
                  <Grid key={img.roomTypeName} size={{ xs: 12, md: 2 }}>
                    <Card
                      sx={{
                        backgroundColor: currentTypeRoom === img.roomTypeName ? `${colors.gray[100]}` : '',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleRoomType(img.roomTypeName, img.url)}
                    >
                      <CardMedia
                        component="img"
                        height="100"
                        width="100"
                        onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)}
                        image={img.url}
                      />
                      <Typography variant="subtitle2">{img.roomTypeName}</Typography>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Typography variant="subtitle2">{t('modals.virtualStaging.emptyRooms')}</Typography>
            )}
          </Grid>
        </Card>
      </Stack>
    </SkeletonContainer>
  );
};

const Interior = () => {
  const { t } = useLocalization();
  const { getModalData, setModalData } = MODALS_STORE.tasks();
  const taskType = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.task?.type;
  const imgSrc = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.thumbnailUrl ?? '';
  const roomType = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.roomType ?? '';
  const interiorName = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.code ?? '';
  const activeStep = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.activeStep ?? 0;

  const virtualStagingType: VirtualStagingType =
    taskType === TaskCategory.VirtualStagingCommercial || taskType === TaskCategory.VirtaulStagingCommercial720
      ? 'Commercial'
      : 'Residential';

  const { data: virtualStagingData } = virtualStagingApi(virtualStagingType, activeStep === 1);

  const handleInteriorName = (interiorName: string, url: string) => {
    setModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE, {
      ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE),
      virtualStaging: {
        ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE).virtualStaging,
        code: interiorName,
        furniture: url,
      },
    });
  };

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap="1.25rem">
      <Card sx={{ flex: 1 }}>
        <CardTitleHeader title={t('modals.virtualStaging.summaryStep.yourChoiceText')} />
        <CardMedia
          component="img"
          height="200"
          width="200"
          onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)}
          image={imgSrc}
        />
      </Card>
      <Card sx={{ flex: 3 }}>
        <CardTitleHeader title={t('modals.virtualStaging.selectRoomStep.title')} />
        <Grid container gap="1.25rem">
          {virtualStagingData?.length
            ? virtualStagingData
                .find(item => item.roomTypeName === roomType)
                ?.interiorOptions?.map(img => {
                  return (
                    <Grid key={img.name} size={{ xs: 12, md: 2 }}>
                      <Card
                        sx={{
                          backgroundColor: interiorName === img.name ? `${colors.gray[100]}` : '',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleInteriorName(img.name, img.url)}
                      >
                        <CardMedia
                          component="img"
                          height="100"
                          width="100"
                          onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)}
                          image={img.url}
                        />
                        <Typography variant="subtitle2">{img.name}</Typography>
                      </Card>
                    </Grid>
                  );
                })
            : null}
        </Grid>
      </Card>
    </Stack>
  );
};

const Label = () => {
  const { t } = useLocalization();
  const { getModalData, setModalData } = MODALS_STORE.tasks();
  const imgSrc = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.thumbnailUrl ?? '';
  const label = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.label ?? '';

  const handleChange = (event: SelectChangeEvent) => {
    setModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE, {
      ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE),
      virtualStaging: {
        ...getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE).virtualStaging,
        label: event.target.value,
      },
    });
  };

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} gap="1.25rem">
      <Card sx={{ flex: 1 }}>
        <CardTitleHeader title={t('modals.virtualStaging.summaryStep.yourChoiceText')} />
        <CardMedia
          component="img"
          height="200"
          width="200"
          onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)}
          image={imgSrc}
        />
      </Card>
      <Card sx={{ flex: 3, border: 'none !important' }}>
        <CardTitleHeader title={t('modals.virtualStaging.selectLabelStep.title')} />
        <Typography variant="subtitle2">{t('modals.virtualStaging.selectLabelStep.instructions')}</Typography>
        <Stack
          sx={{
            width: '100%',
          }}
        >
          <FormControl>
            <InputLabel id="label">{t('modals.virtualStaging.selectLabelStep.stepTitle')}</InputLabel>
            <Select value={label} label={t('modals.virtualStaging.selectLabelStep.stepTitle')} onChange={handleChange}>
              {interiorOptions.map(option => (
                <MenuItem key={option} value={t(`tasks.virtualStagingLabels.${option}`)}>
                  {option ? t(`tasks.virtualStagingLabels.${option}`) : '--'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Card>
    </Stack>
  );
};

const SummaryChoices = () => {
  const { t } = useLocalization();
  const { getModalData } = MODALS_STORE.tasks();

  const imgSrc = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.thumbnailUrl ?? '';
  const selectedRoomSrc = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.roomTypeUrl ?? '';
  const selectedInteriorSrc = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.furniture ?? '';
  const interiorCode = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.code ?? '';
  const roomType = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.roomType ?? '';
  const label = getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.label ?? '';
  const thumbnailDescription =
    getModalData(TASKS_MODAL_ID.VIRTUAL_STAGING_PICTURE)?.virtualStaging?.thumbnailDescription ?? '';

  const selectedItems = [
    { title: t('modals.virtualStaging.selectedImageTitle'), url: imgSrc, type: thumbnailDescription },
    { title: t('modals.virtualStaging.selectedRoomTitle'), url: selectedRoomSrc, type: roomType },
    { title: t('modals.virtualStaging.selectedFurnitureTitle'), url: selectedInteriorSrc, type: interiorCode },
  ];

  return (
    <Stack gap="0.75rem">
      <Typography variant="subtitle1">{t('modals.virtualStaging.summaryStep.title')}</Typography>
      <Typography variant="subtitle2">{t('modals.virtualStaging.summaryStep.instructions')}</Typography>
      <Stack direction="row" gap="1.25rem">
        {selectedItems.map(img => (
          <Card key={img.title} sx={{ flex: 1 }}>
            <CardTitleHeader title={img.title} />
            <CardMedia component="img" onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)} image={img.url} />
            <Typography variant="subtitle2">{img?.type}</Typography>
          </Card>
        ))}
        {label && (
          <Card sx={{ flex: 1 }}>
            <CardTitleHeader title={t('modals.virtualStaging.labelPreviewTitle')} />
            <Box sx={{ position: 'relative' }}>
              <CardMedia component="img" onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)} image={imgSrc} />
              <Chip
                label={label}
                size="small"
                sx={{
                  position: 'absolute',
                  top: '0.75rem',
                  right: '0.75rem',
                  textTransform: 'capitalize',
                  color: colors.base.white,
                  backgroundColor: colors.primary.main,
                }}
              />
            </Box>
          </Card>
        )}
      </Stack>
    </Stack>
  );
};

const virtualStagingModalContent = [
  { titleKey: 'modals.virtualStaging.selectItemStep.title', content: <SelectItem /> },
  { titleKey: 'modals.virtualStaging.selectRoomStep.stepTitle', content: <RoomType /> },
  { titleKey: 'modals.virtualStaging.selectInteriorStep.stepTitle', content: <Interior /> },
  { titleKey: 'modals.virtualStaging.selectLabelStep.stepTitle', content: <Label /> },
  { titleKey: 'modals.virtualStaging.summaryStep.stepTitle', content: <SummaryChoices /> },
];

export default virtualStagingModalContent;
